import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, inject, output } from '@angular/core';
import {MealKitIconButtonComponent} from '@yoni/mealcomponents-lib';

import {TranslateModule} from '@ngx-translate/core';
import {Subscription} from 'rxjs';
import {PipeNumberFormat} from 'app/Components/Pipes/PipeNumberFormat';
import {AuthService, User} from "../../../Services/auth.service";
import {ModalService} from "../../../Services/modal.service";
import {Company, Enterprise, GestionService, OrderHistoryObject} from "../../../Services/gestion.service";
import {ToastrService} from "ngx-toastr";
import {FormsModule} from "@angular/forms";
import {ActionButtonComponent} from "../../action-button/action-button.component";
import {Router} from "@angular/router";
import {QdmService} from "../../../Services/qdm.service";
import {ModalOrderNotationComponent} from "../ModalOrderNotation/ModalOrderNotation.component";
import {LocalStorageHelper} from "../../../Utils/LocalStorageHelper";
import {AccordeonComponent} from "../../Accordeon/accordeon.component";
import {OrderHistoryService} from 'app/Services/order-history.service';

@Component({
  selector: 'modal-cart-checkout',
  templateUrl: './ModalCommandsHistory.component.html',
  styleUrl: "./ModalCommandsHistory.component.scss",
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    MealKitIconButtonComponent,
    TranslateModule,
    PipeNumberFormat,
    FormsModule,
    ActionButtonComponent,
    AccordeonComponent
  ]
})
export class ModalCommandsHistoryComponent implements OnDestroy, AfterViewInit {
  private variables = inject<any>('variables' as any);
  private auth = inject(AuthService);
  private modal = inject(ModalService);
  private cdr = inject(ChangeDetectorRef);
  private gestion = inject(GestionService);
  private toast = inject(ToastrService);
  private router = inject(Router);
  private qdm = inject(QdmService);
  private orderHistoryService = inject(OrderHistoryService);


  readonly messageEvent = output<void>();

  private sub: Subscription = new Subscription();

  user: User | undefined = undefined;
  protected history: OrderHistoryObject[] = [];
  protected filteredHistory: OrderHistoryObject[] = [];

  protected _form_SearchFilter: string = "";
  protected company: Company | null = null;
  protected historyWithData: OrderHistoryObject[] = [];
  private orderToShowId: number | undefined = undefined;
  protected acceptPayments: boolean = false;
  private enterprise: Enterprise | undefined = undefined;
  private forceRefresh: boolean = false;

  ngOnInit(): void {
    this.orderToShowId = this.variables.orderToShow;
    this.forceRefresh = this.variables.forceRefresh || false;

    this.sub.add(this.qdm.selectedEnterprise$.subscribe(ent => {
      this.enterprise = ent;
      this.cdr.detectChanges();
    }));
  }

  ngAfterViewInit(): void {
    this.sub.add(this.auth.userSubject.subscribe(user => {
      this.user = user;
      this.cdr.detectChanges();

      if (this.user) {
        let value = this.auth.userEnterpriseSubject.value;
        if (value) {
          let restaurant = this.qdm.selectedRestaurantSubject.value;
          if (!restaurant) return;

          const restoId = restaurant.id;
          const cstId = restaurant.cstID;

          this.gestion.fetchCommandsHistory(value.id, this.forceRefresh).then(r => {
            this.gestion.fetchCompany(restoId, cstId).then(company => {
              this.history = r.sort((a, b) => {
                return new Date(b.deliveryDate).getTime() - new Date(a.deliveryDate).getTime();
              }).filter(h => (h.company as any).id === company.id);
              this.filteredHistory = this.history;
              this.company = company;

              this.acceptPayments = this.enterprise?.isAcceptPayment || false;
              this.historyWithData = this.history.filter(h => LocalStorageHelper.order_hasAnyData(h.id));
              if (this.orderToShowId) {
                this._form_SearchFilter = this.orderToShowId.toString();
                this.updateSearch();
              }

              this.cdr.detectChanges();
            });

          });
        }
      }

      this.cdr.detectChanges();
    }));


  }

  ngOnDestroy(): void {
    this.sub.unsubscribe();
  }

  close(ev: MouseEvent | null) {
    if (ev) {
      if (ev.target == ev.currentTarget) {
        this.modal.close();
      }
    } else {
      this.modal.close();
    }
  }


  formatDate(d: string): string {
    let date = new Date(d);

    return date.toLocaleDateString("fr-fr");
  }


  getUsername(): string {
    if (!this.user) return "";

    return this.user.firstname + " " + this.user.lastname.toUpperCase();
  }

  computeStatusState(status: OrderHistoryObject): { classes: string; name: string; icon: string } | null {
    switch (status.status) {
      case "cancelled":
        return {
          classes: "bg-rose-1 text-white",
          name: "Annulée",
          icon: "close"
        }
      case "reimbursed":
        return {
          classes: "bg-orange-1 text-white",
          name: (this.company?.isAcceptPayment || false) ? "Remboursée" : "Annulée",
          icon: "euro"
        }
      case "valid":
        return {
          classes: "bg-vert-1 text-white",
          name: "Validée",
          icon: "check"
        }
    }

    return null;
  }

  isCancellable(history: OrderHistoryObject): boolean {
    if (history.status !== "valid" || !this.company?.numberOfDaysForCancelation || this.company?.numberOfDaysForCancelation <= -1) {
      return false;
    }

    const maxDaysForCancellation = this.company?.numberOfDaysForCancelation;
    if (!maxDaysForCancellation || maxDaysForCancellation === 0) {
      return false;
    }

    const deliveryDate = new Date(Date.parse(history.deliveryDate));
    deliveryDate.setHours(0, 0, 0, 0);

    const cancelDeadline = new Date(deliveryDate);
    cancelDeadline.setDate(deliveryDate.getDate() - maxDaysForCancellation);

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return today <= cancelDeadline;
  }

  hasFacture(history: OrderHistoryObject): boolean {
    return history.status === "valid" && history.isPaid;
  }

  wip() {
    this.toast.warning("Fonctionnalité en cours de développement", "WIP", {positionClass: 'toast-top-center'});
  }

  updateSearch() {
    if (this._form_SearchFilter.trim().length == 0) {
      this.filteredHistory = this.history;
      return;
    }

    const s = this._form_SearchFilter.toLowerCase();
    this.filteredHistory = this.history.filter(h => {
      return this.contains(h, s);
    });

    this.cdr.detectChanges();
  }

  contains(h: OrderHistoryObject, value: string): boolean {
    if (h.id.toString().toLowerCase().includes(value)
      || h.status.toLowerCase().includes(value)
      || h.deliveryDate.toLowerCase().includes(value)
      || h.createdAt.toLowerCase().includes(value)
      || h.price.toString().toLowerCase().includes(value)
      || h.isPaid.toString().toLowerCase().includes(value)
    ) {
      return true;
    }

    if (h.child_name && h.child_name.toLowerCase().includes(value)) {
      return true;
    }

    return this.getUsername().toLowerCase().includes(value);
  }


  _handler_cancelOrder() {
    const inst: ActionButtonComponent = (this as unknown as ActionButtonComponent);

    inst._state.set("loading");
    inst._displayedLabel.set("Annulation...");
    if (!(window as any).cancellationCount) {
      (window as any).cancellationCount = 0;
    }

    (window as any).cancellationCount += 1;

    const id = inst.data().id;

    this.gestion.cancelOrder(id).then(r => {
      if (r.success) {
        inst._state.set("success");
        inst._displayedLabel.set("Commande annulée");
        (window as any).cancellationCount -= 1;
        setTimeout(() => {
          inst.reset();

          if ((window as any).cancellationCount <= 0) {
            this.modal.close();
            this.orderHistoryService._orderHistorySubject.next(Math.random());
            this.modal.open(ModalCommandsHistoryComponent, {
              forceRefresh: true
            }, () => {

            });
          }
        }, 2000);
      }
    }).catch(() => {
      inst._state.set("failed");
      inst._displayedLabel.set("Impossible d'annuler");

      setTimeout(() => {
        inst.reset();
      }, 2000);
    });
  }

  _handler_facture() {
    // context's this is an instance of ActionButtonComponent
    const inst: ActionButtonComponent = (this as unknown as ActionButtonComponent);

    inst._state.set("loading");
    inst._displayedLabel.set("Téléchargement...");

    const data = inst.data();
    if (data && typeof data.createdAt === 'string') {
      this.gestion.fetchFacture(data.id).then(r => {
        if (r.success) {
          window.open(r.msg, '_blank');
        } else {
          this.toast.error("Impossible de récupérer la facture", "Erreur", {positionClass: 'toast-top-center'});
        }

        inst._displayedLabel.set(r.success ? "Ouverture..." : "Erreur");
        inst._state.set(r.success ? "success" : "failed");

        setTimeout(() => {
          inst.reset();
        }, 2000);
      })
        .catch(() => {
          inst._displayedLabel.set("Erreur");
          inst._state.set("failed");

          setTimeout(() => {
            inst.reset();
          }, 2000);
        });
    }
  }

  _handler_openNotation() {
    // context's this is an instance of ActionButtonComponent
    const inst: ActionButtonComponent = (this as unknown as ActionButtonComponent);

    this.modal.open(ModalOrderNotationComponent, {order: inst.data()}, () => {
    });

  }
}
